import * as Sentry from '@sentry/react';
import { selectIceRelay, selectIceRelayFallback } from 'features/application/applicationSlice';
import { openModal } from 'features/modal/modalSlice';
import { JanusJS } from 'lib/janus';
import { store } from 'store/store';
import { eventBus } from 'utils/eventBus';
import { formatErrorForLogging, logger } from 'utils/logger';
import { RTCClient } from 'utils/webrtc';
import { prepareIceRestart } from 'utils/webrtc/prepareIceRestart';
import { DtlsStallRecovery } from 'utils/webrtc/DtlsStallRecovery';
import { isTurnCredentialRefused } from 'utils/webrtc/turnCredentials';
import { IceLogger } from 'utils/webrtc/IceLogger';
import {
  IceDeathState,
  IceReportDecision,
  IceRestartDecision,
} from 'utils/webrtc/IceRecoveryLadder';
import { IceDeathSequencer } from 'utils/webrtc/iceDeathSequencer';
import { PublishingKind, PublishingNegotiationMeta, PublishOffer } from 'utils/webrtc/types';
import { ControlledPublishingHandle } from 'utils/webrtc/publishing/ControlledPublishingHandle';

// Host members are read at call time, never captured: `handle` is reassigned per attach,
// `reconnectionReset` swaps `negotiationMeta` wholesale, and tests monkey-patch `requestPublish`.
export type HealthMonitorHost = {
  handle?: ControlledPublishingHandle;
  kind: PublishingKind;
  readonly disposed: boolean;
  negotiationMeta: PublishingNegotiationMeta;
  requestPublish: (offerParams?: PublishOffer) => Promise<void>;
  getPeerConnection: () => RTCPeerConnection | undefined;
  rebuildOnFreshHandle: () => Promise<boolean>;
};

export class ConnectionHealthMonitor {
  private readonly iceDeath = new IceDeathSequencer({
    isSettled: () => this.host.negotiationMeta.mediaFailureReported,
    dtlsNeverCompleted: () => this.dtlsRecovery.onIceDisconnected(),
    onReport: (decision, state) => this.onLadderReport(decision, state),
    onRestart: (decision, state) => this.restartIce(decision, state),
  });

  // Read by `onReport`, which runs synchronously inside `onIceDeath`.
  private dyingIceStats?: JanusJS.IceConnectionStats;

  readonly dtlsRecovery: DtlsStallRecovery;

  constructor(private host: HealthMonitorHost) {
    this.dtlsRecovery = new DtlsStallRecovery({
      feedType: host.kind,
      canRecover: () =>
        !!this.host.handle &&
        !this.host.disposed &&
        !RTCClient.supressErrors &&
        this.isActiveFeed() &&
        !this.host.negotiationMeta.mediaFailureReported,
      canRebuild: () => this.host.kind === 'publishing',
      connectionStateSupported: () => this.host.getPeerConnection()?.connectionState !== undefined,
      rebuild: () => this.host.rebuildOnFreshHandle(),
      cancelIceRestart: this.clearRestartTimer,
      reportFailure: () =>
        this.reportMediaFailure('connection_failed', 'failed', { previouslyConnected: true }),
    });
  }

  private isActiveFeed = (): boolean =>
    Object.is(RTCClient.publishingFeed, this.host) ||
    Object.is(RTCClient.screensharingFeed, this.host);

  // Base `dispose`/`handleCleanup` must call this, or a restart scheduled just
  // before teardown fires on the dead feed.
  clearRestartTimer = () => {
    this.iceDeath.dispose();
  };

  // The PC is gone: a fresh budget for the next publish. Both per-PC latches go
  // with it, or deaths are ignored forever and the next teardown reports a false cause.
  resetForNewPeerConnection = () => {
    this.iceDeath.reset();
    this.host.negotiationMeta.mediaFailureReported = false;
    this.host.negotiationMeta.iceRestartAttempt = false;
  };

  onIceState = (state: RTCIceConnectionState, stats?: JanusJS.IceConnectionStats) => {
    if (!this.host.handle?.handle) {
      return;
    }

    // Superseded by Client.reset(): this PC is orphaned and would falsely classify as
    // network_blocked. Drop the event so it stays out of the log, Sentry, and modal.
    if (!this.isActiveFeed()) {
      return;
    }

    if (this.host.kind === 'screensharing') {
      logger.remote().debug(`[SCREENSHARE-DEBUG] ICE state: ${state}`, { hidden: document.hidden });
    }

    const peerConnection = this.host.getPeerConnection();

    if ((state === 'connected' || state === 'completed') && stats) {
      IceLogger.logConnectionSuccess(
        this.host.handle.handle,
        this.host.kind,
        stats,
        peerConnection
      );
    } else if (state === 'failed' || state === 'disconnected') {
      IceLogger.logConnectionFailure(this.host.handle.handle, this.host.kind, state, stats);
    } else {
      IceLogger.logConnectionState(this.host.handle.handle, this.host.kind, state);
    }

    if (state === 'connected' || state === 'completed') {
      this.dtlsRecovery.onIceConnected();
      this.iceDeath.onIceConnected();
      this.host.negotiationMeta.iceRestartAttempt = false;

      // Engines pass through 'connected' before 'completed'; log the relay once.
      if (state === 'connected' && this.iceDeath.ladder.isRelayActive) {
        IceLogger.logRelayConnected(this.host.handle.handle, this.host.kind, peerConnection);
      }
    }

    if (state === 'disconnected' || state === 'failed') {
      this.dyingIceStats = stats;
      this.iceDeath.onIceDeath(state, stats?.previouslyConnected ?? false);
    }
  };

  private onLadderReport = (decision: IceReportDecision, state: IceDeathState) => {
    this.reportMediaFailure(
      decision.cause,
      state,
      this.dyingIceStats,
      decision.connectedLifetimeMs
    );
  };

  private restartIce = async (decision: IceRestartDecision, reason: IceDeathState) => {
    if (this.host.disposed || !this.host.handle) {
      return;
    }

    // Read before the await: handleCleanup can drop the handle while the
    // restart is in flight.
    const handleId = this.host.handle.handle;

    logger
      .remote({ system: true, capture: 'streaming' })
      .warn(`${this.host.kind} peer connection ${reason}. Trying to recover...`, {
        handleId,
        relay: decision.relay,
        // prepareIceRestart skips relay silently when the flag is off, so without
        // this a disabled run is indistinguishable from a rung that declined relay.
        relayFallbackEnabled: selectIceRelayFallback(store.getState()),
        connectedLifetimeMs: decision.connectedLifetimeMs,
        stableReset: decision.stableReset,
        ...this.iceDeath.ladder.snapshot(),
      });

    prepareIceRestart({
      pc: this.host.getPeerConnection(),
      ladder: this.iceDeath.ladder,
      decision,
      handleId,
      feedType: this.host.kind,
      reason,
    });

    this.host.negotiationMeta.iceRestartAttempt = true;

    try {
      await this.host.requestPublish({ iceRestart: true });
    } catch (error) {
      if (RTCClient.supressErrors) {
        return;
      }

      const message = 'ICE restart request failed';
      const context = {
        handleId,
        direction: 'publishing',
        reason,
      };

      logger.remote({ system: true, capture: 'streaming' }).error(message, context);

      Sentry.captureException(new Error(message, { cause: error }), {
        tags: { component: 'BasePublishing', operation: 'ice-restart' },
        contexts: { iceRestart: { ...context, ...formatErrorForLogging(error) } },
      });
    }
  };

  onIceGatheringState = (state: RTCIceGatheringState, candidateStats?: any) => {
    if (!this.host.handle?.handle) {
      return;
    }

    if (state !== 'complete') {
      return;
    }

    const peerConnection = this.host.getPeerConnection();
    const iceServers = peerConnection?.getConfiguration()?.iceServers;
    IceLogger.logGatheringComplete(
      this.host.handle.handle,
      this.host.kind,
      candidateStats,
      iceServers,
      peerConnection
    );

    // Chrome enumerates host/mDNS candidates with no network access, so exactly 0
    // proves the PC is wedged in 'new' with no death for onIceState to react to.
    if (candidateStats?.gathered !== 0) {
      return;
    }

    if (RTCClient.supressErrors || !this.isActiveFeed()) {
      return;
    }

    // A renegotiation can re-gather nothing (Firefox bundling a new m-line), and a live PC keeps
    // its join-time credential: neither the empty gathering nor that expiry means it failed.
    const iceState = peerConnection?.iceConnectionState;
    if (iceState === 'connected' || iceState === 'completed') {
      return;
    }

    const { hasConnected } = this.iceDeath.ladder;
    // An empty restart is the ladder's to retry only on the PC that connected (a fresh PC parks in
    // 'new'); a refused/expired TURN credential ends recovery anyway, as Rejoin gets a fresh one.
    if (
      this.dtlsRecovery.iceEverConnected &&
      !isTurnCredentialRefused(candidateStats.candidateErrors, iceServers)
    ) {
      return;
    }

    this.reportMediaFailure(
      hasConnected ? 'connection_failed' : 'network_blocked',
      iceState ?? 'new',
      {
        gathered: 0,
        nonHostGathered: 0,
        previouslyConnected: hasConnected,
        candidateErrors: candidateStats.candidateErrors,
      }
    );
  };

  onConnectionState = (state: RTCPeerConnectionState) => {
    if (!this.host.handle?.handle) {
      return;
    }

    if (!this.isActiveFeed()) {
      return;
    }

    this.dtlsRecovery.onConnectionState(state);
  };

  // Callers must gate on `RTCClient.supressErrors`; the helper only dedupes
  // via `mediaFailureReported`.
  reportMediaFailure = (
    cause: 'connection_failed' | 'network_blocked',
    state: RTCIceConnectionState,
    stats?: Partial<JanusJS.IceConnectionStats>,
    connectedLifetimeMs: number | null = null
  ) => {
    if (this.host.negotiationMeta.mediaFailureReported) {
      return;
    }

    const { handle } = this.host;
    if (!handle) {
      return;
    }

    const direction = 'publishing' as const;
    const pluginType = this.host.kind;
    const handleId = handle.handle;
    const iceRelay = selectIceRelay(store.getState());
    const message = 'Publishing peer connection failed';
    const iceFallback = { ...this.iceDeath.ladder.snapshot(), connectedLifetimeMs };
    const turnCredentialRefused = isTurnCredentialRefused(
      stats?.candidateErrors,
      this.host.getPeerConnection()?.getConfiguration()?.iceServers
    );
    // A refused or expired TURN credential is not a network block: Rejoin fetches a fresh one.
    const reportedCause =
      cause === 'network_blocked' && turnCredentialRefused ? 'connection_failed' : cause;

    const candidatesGathered = stats?.gathered ?? 0;
    const nonHostCandidatesGathered = stats?.nonHostGathered ?? 0;
    const previouslyConnected = stats?.previouslyConnected ?? false;

    logger.remote({ capture: 'streaming' }).error(message, {
      handleId,
      pluginType,
      direction,
      iceConnectionState: state,
      candidatesGathered,
      nonHostCandidatesGathered,
      previouslyConnected,
      iceCompleted: stats?.iceCompleted ?? false,
      durationMs: stats?.duration,
      iceRelay,
      iceFallback,
      cause: reportedCause,
      turnCredentialRefused,
    });

    Sentry.captureException(new Error(message), {
      contexts: {
        mediaFailure: {
          cause: reportedCause,
          turnCredentialRefused,
          direction,
          pluginType,
          iceRelay,
          iceConnectionState: state,
          candidatesGathered,
          nonHostCandidatesGathered,
          previouslyConnected,
          iceCompleted: stats?.iceCompleted ?? false,
          durationMs: stats?.duration,
          handleId,
        },
        iceFallback,
      },
    });

    eventBus.sendMessage('mediaConnectionFailed', {}, { direction, cause: reportedCause });
    store.dispatch(openModal('mediaConnectionFailed', { cause: reportedCause, direction }));

    this.host.negotiationMeta.mediaFailureReported = true;
  };

  // Janus tears the PC down before Chrome re-emits a terminal `iceConnectionState`, and its
  // `reason` is the only server-side verdict on a PC that never completed DTLS.
  onWebRTCConnectionDown = (rawReason?: string) => {
    if (RTCClient.supressErrors) {
      return;
    }

    // Superseded by Client.reset(): drop the event so this orphan can't
    // open the modal when Janus tears down its handle.
    if (!this.isActiveFeed()) {
      return;
    }

    // After the teardown guards on purpose: leave/reset hang up every handle.
    IceLogger.logPeerConnectionDown(this.host.handle?.handle, this.host.kind, {
      reason: rawReason,
      iceRestartAttempt: this.host.negotiationMeta.iceRestartAttempt,
      mediaFailureReported: this.host.negotiationMeta.mediaFailureReported,
    });

    this.dtlsRecovery.routeHangup(rawReason, this.host.negotiationMeta);
  };
}
